import React, { useState, useEffect } from 'react';
import {
  Store, CreditCard, Receipt, Clock, CheckCircle, XCircle, AlertTriangle,
  Upload, FileText, Calendar, DollarSign, RefreshCw, ArrowLeft,
  Mail, Phone, MapPin, Globe, Eye, EyeOff, Image, Crown, Zap,
  AlertCircle, Check, X, Building2, History, Send, Megaphone,
  Target, TrendingUp, Monitor, Smartphone, Percent, ChevronDown
} from 'lucide-react';
import { db, subscriptions, payments, appSettings, bannerAdPackages, bannerAdRequests, storage } from '../lib/supabase';
import supabase from '../lib/supabase';

// Format currency for display
const formatCurrency = (amount, currency = 'JMD') => {
  return new Intl.NumberFormat('en-JM', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  }).format(amount);
};

// Format date
const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-JM', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Tier configuration
const TIER_CONFIG = {
  free: {
    name: 'Free',
    color: 'gray',
    icon: Building2,
    benefits: ['Basic listing', 'Name & contact info only']
  },
  basic: {
    name: 'Basic',
    color: 'blue',
    icon: Zap,
    benefits: ['Logo display', 'Full description', 'Services list', 'Website link', 'Email support']
  },
  premium: {
    name: 'Premium',
    color: 'purple',
    icon: Crown,
    benefits: ['Everything in Basic', 'Priority placement', 'Analytics dashboard', 'Featured badge', 'Priority support']
  }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    active: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle, label: 'Active' },
    pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: 'Pending Payment' },
    grace_period: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle, label: 'Grace Period' },
    suspended: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Suspended' },
    cancelled: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: X, label: 'Cancelled' }
  };

  const cfg = config[status] || config.pending;
  const Icon = cfg.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${cfg.color}`}>
      <Icon className="w-4 h-4" />
      {cfg.label}
    </span>
  );
};

// ==================== VENDOR PORTAL PAGE ====================
// Supports two access modes:
// 1. Token-based access (from email link) - uses token prop
// 2. Direct access (logged-in user) - uses vendorId and providerTable props
export const VendorPortalPage = ({ setPage, token, vendorId, providerTable }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState({});

  // Payment request form state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    payment_method: 'bank_transfer',
    transaction_ref: '',
    payment_date: new Date().toISOString().split('T')[0],
    proof_file: null
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Banner ad state
  const [bannerPackages, setBannerPackages] = useState([]);
  const [bannerRequests, setBannerRequests] = useState([]);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [bannerForm, setBannerForm] = useState({
    placement: '',
    duration: 0,
    notes: '',
    targetPages: ['home'],
    linkUrl: '',
    altText: '',
    desktopImage: null,
    mobileImage: null,
    payWithListing: false
  });
  const [bannerSubmitting, setBannerSubmitting] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [desktopPreview, setDesktopPreview] = useState(null);
  const [mobilePreview, setMobilePreview] = useState(null);

  // Available pages for banner ads
  const AVAILABLE_PAGES = [
    { id: 'home', label: 'Home Page' },
    { id: 'offences', label: 'Offences & Fines' },
    { id: 'services', label: 'Service Providers' },
    { id: 'lawyers', label: 'Lawyers Directory' },
    { id: 'insurance', label: 'Insurance Companies' },
    { id: 'evaluators', label: 'Vehicle Evaluators' },
    { id: 'mechanics', label: 'Mechanics Directory' }
  ];

  // Banner dimension requirements by placement
  const BANNER_DIMENSIONS = {
    top: { desktop: '728×90 pixels', mobile: '320×50 pixels', label: 'Leaderboard' },
    bottom: { desktop: '728×90 pixels', mobile: '320×50 pixels', label: 'Leaderboard' },
    inline: { desktop: '300×250 pixels', mobile: '300×250 pixels', label: 'Medium Rectangle' }
  };

  useEffect(() => {
    if (token) {
      loadVendorDataByToken();
    } else if (vendorId) {
      loadVendorDataDirect();
    } else {
      setError('No access token provided. Please use the link from your email.');
      setLoading(false);
    }
  }, [token, vendorId]);

  // Load vendor data using token validation (email link access)
  const loadVendorDataByToken = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate token and get vendor info
      const { data: tokenData, error: tokenError } = await supabase.rpc('validate_vendor_access_token', {
        p_token: token
      });

      if (tokenError) throw tokenError;
      if (!tokenData || tokenData.length === 0) {
        throw new Error('Invalid or expired access token. Please contact support for a new link.');
      }

      const vendorInfo = tokenData[0];
      if (!vendorInfo.is_valid) {
        throw new Error('Your access link has expired. Please contact support for a new link.');
      }

      // Load full vendor data
      const { data: vendorData, error: vendorError } = await supabase
        .from('service_providers')
        .select('*')
        .eq('id', vendorInfo.vendor_id)
        .single();

      if (vendorError) throw vendorError;
      setVendor(vendorData);

      // Load related data
      await loadRelatedData(vendorInfo.vendor_id);

    } catch (err) {
      console.error('Error loading vendor data:', err);
      setError(err.message || 'Failed to load your account information.');
    } finally {
      setLoading(false);
    }
  };

  // Load vendor data directly by ID (logged-in user access)
  const loadVendorDataDirect = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get current user to verify ownership
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to access this portal.');
      }

      // Determine the table to query based on providerTable prop
      const tableName = providerTable || 'service_providers';

      // Load vendor data directly from the appropriate table WITH owner verification
      const { data: vendorData, error: vendorError } = await supabase
        .from(tableName)
        .select('*')
        .eq('id', vendorId)
        .eq('owner_id', user.id)  // SECURITY FIX: Verify user owns this vendor
        .single();

      if (vendorError) throw vendorError;
      if (!vendorData) {
        throw new Error('Business listing not found or you do not have permission to access it.');
      }

      // Add type info if not present (for non service_providers tables)
      const enrichedVendor = { ...vendorData };
      if (!enrichedVendor.type) {
        if (tableName === 'lawyers') enrichedVendor.type = 'lawyer';
        else if (tableName === 'insurance_companies') enrichedVendor.type = 'insurance';
        else if (tableName === 'vehicle_evaluators') enrichedVendor.type = 'evaluator';
      }

      setVendor(enrichedVendor);

      // Load related data (only for service_providers for now)
      if (tableName === 'service_providers') {
        await loadRelatedData(vendorId);
      } else {
        // For other tables, just load payment settings
        await loadPaymentSettings();
      }

    } catch (err) {
      console.error('Error loading vendor data:', err);
      setError(err.message || 'Failed to load your account information.');
    } finally {
      setLoading(false);
    }
  };

  // Load related subscription, payment, and banner data
  const loadRelatedData = async (providerId) => {
    const [subRes, paymentsRes, requestsRes, bannerPkgRes, bannerReqRes] = await Promise.all([
      subscriptions.getByVendorId(providerId),
      payments.getByVendorId(providerId),
      supabase
        .from('payment_requests')
        .select('*')
        .eq('vendor_id', providerId)
        .order('created_at', { ascending: false }),
      bannerAdPackages.getAll(),
      supabase
        .from('banner_ad_requests')
        .select('*')
        .eq('service_provider_id', providerId)
        .order('created_at', { ascending: false })
    ]);

    setSubscription(subRes.data);
    setPaymentHistory(paymentsRes.data || []);
    setPaymentRequests(requestsRes.data || []);
    setBannerPackages(bannerPkgRes.data || []);
    setBannerRequests(bannerReqRes.data || []);

    await loadPaymentSettings();
  };

  // Load payment configuration settings
  const loadPaymentSettings = async () => {
    const [bankName, accountName, accountNumber, bankBranch, supportEmail] = await Promise.all([
      appSettings.get('payment_bank_name'),
      appSettings.get('payment_account_name'),
      appSettings.get('payment_account_number'),
      appSettings.get('payment_bank_branch'),
      appSettings.get('support_email')
    ]);

    setPaymentSettings({
      bank_name: bankName.data ? JSON.parse(bankName.data) : '',
      account_name: accountName.data ? JSON.parse(accountName.data) : '',
      account_number: accountNumber.data ? JSON.parse(accountNumber.data) : '',
      bank_branch: bankBranch.data ? JSON.parse(bankBranch.data) : '',
      support_email: supportEmail.data ? JSON.parse(supportEmail.data) : ''
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];

    if (file.size > maxSize) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload an image (JPG, PNG, GIF) or PDF.');
      return;
    }

    setPaymentForm({ ...paymentForm, proof_file: file });
  };

  const handleSubmitPayment = async () => {
    if (!paymentForm.proof_file && !paymentForm.transaction_ref) {
      alert('Please provide either a proof of payment file or a transaction reference.');
      return;
    }

    setSubmitting(true);

    try {
      let proofUrl = null;
      let proofFilename = null;

      // Upload proof of payment if provided
      if (paymentForm.proof_file) {
        setUploading(true);
        const file = paymentForm.proof_file;
        const fileExt = file.name.split('.').pop();
        const fileName = `payment-proof-${vendor.id}-${Date.now()}.${fileExt}`;
        const filePath = `payment-proofs/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('vendor-files')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('vendor-files')
          .getPublicUrl(filePath);

        proofUrl = urlData.publicUrl;
        proofFilename = file.name;
        setUploading(false);
      }

      // Determine request type
      let requestType = 'renewal';
      if (subscription?.status === 'suspended') {
        requestType = 'reactivation';
      } else if (subscription?.status === 'pending') {
        requestType = 'initial_payment';
      }

      // Create payment request
      const { data: requestData, error: requestError } = await supabase.rpc('create_payment_request', {
        p_vendor_id: vendor.id,
        p_request_type: requestType,
        p_amount: subscription?.amount || 0,
        p_payment_method: paymentForm.payment_method,
        p_transaction_ref: paymentForm.transaction_ref || null,
        p_payment_date: paymentForm.payment_date,
        p_proof_url: proofUrl,
        p_proof_filename: proofFilename
      });

      if (requestError) throw requestError;

      setSubmitSuccess(true);
      setShowPaymentForm(false);
      setPaymentForm({
        payment_method: 'bank_transfer',
        transaction_ref: '',
        payment_date: new Date().toISOString().split('T')[0],
        proof_file: null
      });

      // Reload data
      loadVendorData();

    } catch (err) {
      console.error('Error submitting payment:', err);
      alert('Failed to submit payment request: ' + err.message);
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  // Handle banner artwork upload
  const handleBannerImageUpload = async (e, isMobile = false) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPG, PNG, GIF).');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (event) => {
      if (isMobile) {
        setMobilePreview(event.target.result);
        setBannerForm({ ...bannerForm, mobileImage: file });
      } else {
        setDesktopPreview(event.target.result);
        setBannerForm({ ...bannerForm, desktopImage: file });
      }
    };
    reader.readAsDataURL(file);
  };

  // Upload banner artwork to storage
  const uploadBannerArtwork = async (file, type) => {
    if (!file) return null;

    const fileExt = file.name.split('.').pop();
    const fileName = `banner-${vendor.id}-${type}-${Date.now()}.${fileExt}`;
    const filePath = `banner-ads/${fileName}`;

    const { error } = await supabase.storage
      .from('vendor-files')
      .upload(filePath, file);

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('vendor-files')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  };

  // Check if can submit new payment request
  const canSubmitPayment = () => {
    if (!subscription) return false;
    // Can submit if suspended, in grace period, or pending
    if (!['suspended', 'grace_period', 'pending'].includes(subscription.status)) return false;
    // Check if there's already a pending request
    const hasPendingRequest = paymentRequests.some(r => r.status === 'pending');
    return !hasPendingRequest;
  };

  const hasPendingRequest = paymentRequests.some(r => r.status === 'pending');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href={`mailto:${paymentSettings.support_email || 'info@jamaicadriversguide.com'}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium"
          >
            <Mail className="w-5 h-5" />
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  const tierConfig = TIER_CONFIG[subscription?.tier || vendor?.tier || 'free'];
  const TierIcon = tierConfig.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-2 text-emerald-100 text-sm mb-4">
            <Store className="w-4 h-4" />
            Jamaica Motorist Guide - Vendor Portal
          </div>
          <h1 className="text-3xl font-black mb-2">{vendor?.name}</h1>
          <div className="flex flex-wrap items-center gap-4 text-emerald-100">
            {vendor?.email && (
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {vendor.email}
              </span>
            )}
            {vendor?.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {vendor.phone}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Success Message */}
        {submitSuccess && (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-emerald-800">Payment Request Submitted!</h3>
              <p className="text-emerald-700 text-sm mt-1">
                We've received your payment proof and will verify it within 24 hours.
                You'll receive an email once your listing is reactivated.
              </p>
            </div>
            <button onClick={() => setSubmitSuccess(false)} className="text-emerald-600 hover:text-emerald-800">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Listing Status Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className={`p-4 ${
            subscription?.status === 'active' ? 'bg-emerald-500' :
            subscription?.status === 'suspended' ? 'bg-red-500' :
            subscription?.status === 'grace_period' ? 'bg-orange-500' :
            'bg-amber-500'
          } text-white`}>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2">
                {vendor?.is_active ? <Eye className="w-6 h-6" /> : <EyeOff className="w-6 h-6" />}
                Listing Status
              </h2>
              <StatusBadge status={subscription?.status || 'pending'} />
            </div>
          </div>

          <div className="p-6">
            {/* Status-specific messages */}
            {subscription?.status === 'suspended' && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Your Listing is Suspended
                </h3>
                <p className="text-red-700 text-sm mt-2">
                  Your listing is currently hidden from the directory due to missed payment.
                  To reactivate your listing, please submit your payment proof below.
                </p>
              </div>
            )}

            {subscription?.status === 'grace_period' && (
              <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-orange-800 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Grace Period Active
                </h3>
                <p className="text-orange-700 text-sm mt-2">
                  Your subscription has expired but your listing is still visible during the grace period.
                  Please make payment by <strong>{formatDate(subscription?.grace_period_end)}</strong> to avoid suspension.
                </p>
              </div>
            )}

            {subscription?.status === 'active' && (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-emerald-800 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Your Listing is Active
                </h3>
                <p className="text-emerald-700 text-sm mt-2">
                  Your listing is visible in the Jamaica Motorist Guide directory.
                  Next renewal due: <strong>{formatDate(subscription?.current_period_end)}</strong>
                </p>
              </div>
            )}

            {/* Subscription Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">Subscription Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <TierIcon className={`w-8 h-8 text-${tierConfig.color}-600`} />
                    <div>
                      <p className="font-bold text-gray-900">{tierConfig.name} Tier</p>
                      <p className="text-sm text-gray-500">
                        {subscription?.billing_period === 'annual' ? 'Annual' : 'Monthly'} billing
                      </p>
                    </div>
                  </div>

                  <div className="text-sm space-y-2">
                    {subscription?.current_period_start && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Current Period:</span>
                        <span className="font-medium">
                          {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Amount:</span>
                      <span className="font-bold text-lg text-emerald-600">
                        {formatCurrency(subscription?.amount || 0)}
                        <span className="text-sm font-normal text-gray-500">
                          /{subscription?.billing_period === 'annual' ? 'year' : 'month'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">Tier Benefits</h3>
                <ul className="space-y-2">
                  {tierConfig.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className={`w-4 h-4 text-${tierConfig.color}-600`} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Request Section */}
        {(subscription?.status === 'suspended' || subscription?.status === 'grace_period' || subscription?.status === 'pending') && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-emerald-600 text-white p-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Upload className="w-6 h-6" />
                Submit Payment Proof
              </h2>
            </div>

            <div className="p-6">
              {hasPendingRequest ? (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                  <h3 className="font-bold text-amber-800 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Payment Verification in Progress
                  </h3>
                  <p className="text-amber-700 text-sm mt-2">
                    We're reviewing your payment proof. You'll receive an email once verified (usually within 24 hours).
                  </p>
                </div>
              ) : showPaymentForm ? (
                <div className="space-y-6">
                  {/* Payment Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h3 className="font-bold text-blue-800 mb-3">Payment Instructions</h3>
                    <div className="text-sm text-blue-700 space-y-2">
                      <p><strong>Bank:</strong> {paymentSettings.bank_name || 'Contact support'}</p>
                      <p><strong>Account Name:</strong> {paymentSettings.account_name || 'Contact support'}</p>
                      <p><strong>Account Number:</strong> {paymentSettings.account_number || 'Contact support'}</p>
                      <p><strong>Branch:</strong> {paymentSettings.bank_branch || 'Contact support'}</p>
                      <p className="mt-3 pt-3 border-t border-blue-200">
                        <strong>Reference:</strong> Use your business name as the payment reference
                      </p>
                    </div>
                  </div>

                  {/* Amount Due */}
                  <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 text-center">
                    <p className="text-emerald-700 text-sm">Amount Due</p>
                    <p className="text-3xl font-black text-emerald-600">
                      {formatCurrency(subscription?.amount || 0)}
                    </p>
                  </div>

                  {/* Form Fields */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method
                      </label>
                      <select
                        value={paymentForm.payment_method}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none"
                      >
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="paypal">PayPal</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Date
                      </label>
                      <input
                        type="date"
                        value={paymentForm.payment_date}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction Reference / Receipt Number
                    </label>
                    <input
                      type="text"
                      value={paymentForm.transaction_ref}
                      onChange={(e) => setPaymentForm({ ...paymentForm, transaction_ref: e.target.value })}
                      placeholder="e.g., Bank ref #, PayPal transaction ID"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Proof of Payment <span className="text-gray-400">(Receipt, Screenshot, etc.)</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-emerald-400 transition-colors">
                      {paymentForm.proof_file ? (
                        <div className="flex items-center justify-center gap-3">
                          <FileText className="w-8 h-8 text-emerald-600" />
                          <div className="text-left">
                            <p className="font-medium text-gray-900">{paymentForm.proof_file.name}</p>
                            <p className="text-sm text-gray-500">
                              {(paymentForm.proof_file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <button
                            onClick={() => setPaymentForm({ ...paymentForm, proof_file: null })}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <X className="w-5 h-5 text-gray-500" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600 font-medium">Click to upload</p>
                          <p className="text-sm text-gray-400 mt-1">JPG, PNG, GIF or PDF (max 5MB)</p>
                          <input
                            type="file"
                            onChange={handleFileUpload}
                            accept="image/*,.pdf"
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleSubmitPayment}
                      disabled={submitting || (!paymentForm.proof_file && !paymentForm.transaction_ref)}
                      className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-bold rounded-xl flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          {uploading ? 'Uploading...' : 'Submitting...'}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Submit Payment Proof
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowPaymentForm(false)}
                      className="px-6 py-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-600 mb-4">
                    Ready to {subscription?.status === 'suspended' ? 'reactivate' : 'renew'} your listing?
                  </p>
                  <button
                    onClick={() => setShowPaymentForm(true)}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-lg"
                  >
                    <CreditCard className="w-6 h-6" />
                    Make Payment & Reactivate
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Request History */}
        {paymentRequests.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gray-700 text-white p-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <History className="w-6 h-6" />
                Payment Requests
              </h2>
            </div>

            <div className="divide-y">
              {paymentRequests.map((request) => (
                <div key={request.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        request.status === 'approved' ? 'bg-emerald-100' :
                        request.status === 'pending' ? 'bg-amber-100' :
                        request.status === 'rejected' ? 'bg-red-100' :
                        'bg-gray-100'
                      }`}>
                        {request.status === 'approved' ? <CheckCircle className="w-5 h-5 text-emerald-600" /> :
                         request.status === 'pending' ? <Clock className="w-5 h-5 text-amber-600" /> :
                         request.status === 'rejected' ? <XCircle className="w-5 h-5 text-red-600" /> :
                         <AlertCircle className="w-5 h-5 text-gray-600" />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {request.request_type === 'reactivation' ? 'Reactivation Request' :
                           request.request_type === 'renewal' ? 'Renewal Payment' :
                           'Initial Payment'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(request.created_at)} • {formatCurrency(request.amount)}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      request.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      request.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </div>
                  {request.status === 'rejected' && request.rejection_reason && (
                    <div className="mt-3 ml-13 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">
                        <strong>Reason:</strong> {request.rejection_reason}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment History */}
        {paymentHistory.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-blue-600 text-white p-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Receipt className="w-6 h-6" />
                Payment History
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Method</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Period</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paymentHistory.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        {formatDate(payment.payment_date)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">
                        {payment.payment_method?.replace('_', ' ')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {payment.period_covered_start && payment.period_covered_end ? (
                          `${new Date(payment.period_covered_start).toLocaleDateString()} - ${new Date(payment.period_covered_end).toLocaleDateString()}`
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          payment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          payment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Banner Advertising Section - Only for Basic/Premium tiers */}
        {(subscription?.tier === 'basic' || subscription?.tier === 'premium' || vendor?.tier === 'basic' || vendor?.tier === 'premium') && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Megaphone className="w-6 h-6" />
                Banner Advertising
              </h2>
              <p className="text-amber-100 text-sm mt-1">Boost your visibility with banner ads across the site</p>
            </div>

            <div className="p-6">
              {/* Existing Banner Ad Requests */}
              {bannerRequests.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold text-gray-700 mb-3">Your Banner Ad Requests</h3>
                  <div className="space-y-3">
                    {bannerRequests.map((req) => (
                      <div key={req.id} className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold text-gray-900 capitalize">{req.placement} Banner - {req.duration_months} Months</p>
                            <p className="text-sm text-gray-500">{formatCurrency(req.final_price)} • Requested {formatDate(req.created_at)}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            req.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                            req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            req.status === 'awaiting_payment' ? 'bg-blue-100 text-blue-700' :
                            req.status === 'payment_submitted' ? 'bg-purple-100 text-purple-700' :
                            req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {req.status === 'awaiting_payment' ? 'Awaiting Payment' :
                             req.status === 'payment_submitted' ? 'Payment Under Review' :
                             req.status.charAt(0).toUpperCase() + req.status.slice(1).replace('_', ' ')}
                          </span>
                        </div>
                        {req.status === 'rejected' && req.rejection_reason && (
                          <p className="mt-2 text-sm text-red-600">Reason: {req.rejection_reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Request New Banner Ad */}
              {!showBannerForm ? (
                <div className="text-center">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-3 bg-amber-50 rounded-xl text-center">
                      <Eye className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                      <p className="font-bold text-gray-900">10,000+</p>
                      <p className="text-xs text-gray-500">Monthly Views</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-xl text-center">
                      <Target className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                      <p className="font-bold text-gray-900">Targeted</p>
                      <p className="text-xs text-gray-500">Motorist Audience</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-xl text-center">
                      <TrendingUp className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                      <p className="font-bold text-gray-900">Analytics</p>
                      <p className="text-xs text-gray-500">Track Performance</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-xl text-center">
                      <Percent className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                      <p className="font-bold text-gray-900">Up to 20%</p>
                      <p className="text-xs text-gray-500">Long-term Discount</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowBannerForm(true)}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl text-lg"
                  >
                    <Megaphone className="w-6 h-6" />
                    Request Banner Ad
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Step 1: Placement Selection */}
                  <div>
                    <label className="block font-bold text-gray-700 mb-3">Step 1: Select Ad Placement</label>
                    <div className="grid md:grid-cols-3 gap-4">
                      {[
                        { id: 'top', label: 'Top Banner', desc: 'Prime position at top of pages', tag: 'PREMIUM', tagColor: 'amber', price: 5000 },
                        { id: 'bottom', label: 'Bottom Banner', desc: 'Footer position on all pages', tag: 'POPULAR', tagColor: 'blue', price: 3333 },
                        { id: 'inline', label: 'In-Content', desc: 'Within page content', tag: 'BEST VALUE', tagColor: 'green', price: 2667 }
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setBannerForm({ ...bannerForm, placement: opt.id })}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            bannerForm.placement === opt.id
                              ? 'border-amber-500 bg-amber-50'
                              : 'border-gray-200 hover:border-amber-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-bold text-${opt.tagColor}-600 bg-${opt.tagColor}-100 px-2 py-1 rounded`}>{opt.tag}</span>
                            <Monitor className="w-5 h-5 text-gray-400" />
                          </div>
                          <h4 className="font-bold">{opt.label}</h4>
                          <p className="text-sm text-gray-500">{opt.desc}</p>
                          <p className="text-lg font-black text-amber-600 mt-2">From J${opt.price.toLocaleString()}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Step 2: Duration Selection */}
                  {bannerForm.placement && (
                    <div>
                      <label className="block font-bold text-gray-700 mb-3">Step 2: Select Duration</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { months: 3, discount: 0 },
                          { months: 6, discount: 10 },
                          { months: 9, discount: 15 },
                          { months: 12, discount: 20, popular: true }
                        ].map((opt) => {
                          const pkg = bannerPackages.find(p => p.placement === bannerForm.placement && p.duration_months === opt.months);
                          const price = pkg?.price || 0;
                          return (
                            <button
                              key={opt.months}
                              type="button"
                              onClick={() => setBannerForm({ ...bannerForm, duration: opt.months })}
                              className={`relative p-4 rounded-xl border-2 text-center ${
                                bannerForm.duration === opt.months
                                  ? 'border-amber-500 bg-amber-50'
                                  : 'border-gray-200 hover:border-amber-300'
                              }`}
                            >
                              {opt.popular && (
                                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">Best</span>
                              )}
                              <p className="font-bold">{opt.months} Months</p>
                              {opt.discount > 0 && <p className="text-xs text-green-600 font-bold">Save {opt.discount}%</p>}
                              <p className="text-xl font-black text-gray-900 mt-1">J${price.toLocaleString()}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Target Pages */}
                  {bannerForm.placement && bannerForm.duration > 0 && (
                    <div>
                      <label className="block font-bold text-gray-700 mb-3">Step 3: Select Target Pages</label>
                      <p className="text-sm text-gray-500 mb-3">Your ad will appear on these pages</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {AVAILABLE_PAGES.map(page => (
                          <label key={page.id} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                            <input
                              type="checkbox"
                              checked={(bannerForm.targetPages || []).includes(page.id)}
                              onChange={(e) => {
                                const pages = bannerForm.targetPages || [];
                                if (e.target.checked) {
                                  setBannerForm({ ...bannerForm, targetPages: [...pages, page.id] });
                                } else {
                                  setBannerForm({ ...bannerForm, targetPages: pages.filter(p => p !== page.id) });
                                }
                              }}
                              className="rounded text-amber-500 focus:ring-amber-500"
                            />
                            <span className="text-sm">{page.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 4: Upload Artwork */}
                  {bannerForm.placement && bannerForm.duration > 0 && (
                    <div>
                      <label className="block font-bold text-gray-700 mb-3">Step 4: Upload Banner Artwork</label>

                      {/* Dimension Requirements */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                        <h4 className="font-bold text-blue-800 flex items-center gap-2 mb-2">
                          <Image className="w-5 h-5" />
                          Artwork Requirements for {BANNER_DIMENSIONS[bannerForm.placement]?.label}
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Monitor className="w-4 h-4 text-blue-600" />
                            <span><strong>Desktop:</strong> {BANNER_DIMENSIONS[bannerForm.placement]?.desktop}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Smartphone className="w-4 h-4 text-blue-600" />
                            <span><strong>Mobile:</strong> {BANNER_DIMENSIONS[bannerForm.placement]?.mobile}</span>
                          </div>
                        </div>
                        <p className="text-xs text-blue-600 mt-2">Accepted formats: JPG, PNG, GIF (max 5MB)</p>
                      </div>

                      {/* Upload Areas */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Desktop Banner */}
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Desktop Banner *</p>
                          <div
                            onClick={() => document.getElementById('desktop-banner-upload')?.click()}
                            className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer hover:border-amber-400 transition-colors"
                          >
                            {desktopPreview ? (
                              <div className="relative">
                                <img src={desktopPreview} alt="Desktop preview" className="max-h-24 max-w-full mx-auto object-contain" />
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDesktopPreview(null); setBannerForm({ ...bannerForm, desktopImage: null }); }}
                                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div className="text-gray-400">
                                <Upload className="w-8 h-8 mx-auto mb-2" />
                                <p className="text-sm font-medium">Click to upload desktop banner</p>
                                <p className="text-xs">{BANNER_DIMENSIONS[bannerForm.placement]?.desktop}</p>
                              </div>
                            )}
                          </div>
                          <input
                            id="desktop-banner-upload"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleBannerImageUpload(e, false)}
                            className="hidden"
                          />
                        </div>

                        {/* Mobile Banner */}
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Mobile Banner (optional)</p>
                          <div
                            onClick={() => document.getElementById('mobile-banner-upload')?.click()}
                            className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer hover:border-amber-400 transition-colors"
                          >
                            {mobilePreview ? (
                              <div className="relative">
                                <img src={mobilePreview} alt="Mobile preview" className="max-h-24 max-w-full mx-auto object-contain" />
                                <button
                                  onClick={(e) => { e.stopPropagation(); setMobilePreview(null); setBannerForm({ ...bannerForm, mobileImage: null }); }}
                                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div className="text-gray-400">
                                <Upload className="w-8 h-8 mx-auto mb-2" />
                                <p className="text-sm font-medium">Click to upload mobile banner</p>
                                <p className="text-xs">{BANNER_DIMENSIONS[bannerForm.placement]?.mobile}</p>
                              </div>
                            )}
                          </div>
                          <input
                            id="mobile-banner-upload"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleBannerImageUpload(e, true)}
                            className="hidden"
                          />
                        </div>
                      </div>

                      <p className="text-xs text-gray-500 mt-2">
                        You can also submit your request without artwork and upload it later, or have our team help design your banner.
                      </p>
                    </div>
                  )}

                  {/* Step 5: Link & Alt Text */}
                  {bannerForm.placement && bannerForm.duration > 0 && (
                    <div>
                      <label className="block font-bold text-gray-700 mb-3">Step 5: Banner Link & Details</label>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Click-through URL *
                          </label>
                          <input
                            type="url"
                            value={bannerForm.linkUrl}
                            onChange={(e) => setBannerForm({ ...bannerForm, linkUrl: e.target.value })}
                            placeholder="https://yourwebsite.com"
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                          />
                          <p className="text-xs text-gray-500 mt-1">Where visitors go when they click your ad</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Alt Text (for accessibility)
                          </label>
                          <input
                            type="text"
                            value={bannerForm.altText}
                            onChange={(e) => setBannerForm({ ...bannerForm, altText: e.target.value })}
                            placeholder="e.g., Special offer from ABC Insurance"
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment Options */}
                  {bannerForm.placement && bannerForm.duration > 0 && (
                    <div>
                      <label className="block font-bold text-gray-700 mb-3">Payment Option</label>
                      <div className="space-y-3">
                        <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          !bannerForm.payWithListing ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'
                        }`}>
                          <input
                            type="radio"
                            checked={!bannerForm.payWithListing}
                            onChange={() => setBannerForm({ ...bannerForm, payWithListing: false })}
                            className="mt-1 text-amber-500 focus:ring-amber-500"
                          />
                          <div>
                            <p className="font-bold text-gray-900">Pay separately for banner ad</p>
                            <p className="text-sm text-gray-500">We'll send you payment instructions after approval</p>
                          </div>
                        </label>
                        <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          bannerForm.payWithListing ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'
                        }`}>
                          <input
                            type="radio"
                            checked={bannerForm.payWithListing}
                            onChange={() => setBannerForm({ ...bannerForm, payWithListing: true })}
                            className="mt-1 text-amber-500 focus:ring-amber-500"
                          />
                          <div>
                            <p className="font-bold text-gray-900">Include with my listing payment</p>
                            <p className="text-sm text-gray-500">Add banner ad cost to your next listing renewal</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {bannerForm.placement && bannerForm.duration > 0 && (
                    <div>
                      <label className="block font-medium text-gray-700 mb-1">Additional Notes (optional)</label>
                      <textarea
                        value={bannerForm.notes}
                        onChange={(e) => setBannerForm({ ...bannerForm, notes: e.target.value })}
                        placeholder="Any specific requirements, questions, or if you need design assistance..."
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                      />
                    </div>
                  )}

                  {/* Price Summary */}
                  {bannerForm.placement && bannerForm.duration > 0 && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-gray-900">{bannerForm.placement.charAt(0).toUpperCase() + bannerForm.placement.slice(1)} Banner - {bannerForm.duration} Months</p>
                          <p className="text-sm text-gray-500">{bannerForm.targetPages?.length || 1} page(s) selected</p>
                        </div>
                        <div className="text-right">
                          {(() => {
                            const pkg = bannerPackages.find(p => p.placement === bannerForm.placement && p.duration_months === bannerForm.duration);
                            const price = pkg?.price || 0;
                            return (
                              <p className="text-2xl font-black text-amber-600">J${price.toLocaleString()}</p>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={async () => {
                        if (!bannerForm.placement || !bannerForm.duration) {
                          alert('Please select a placement and duration');
                          return;
                        }
                        if (!bannerForm.linkUrl) {
                          alert('Please enter the click-through URL for your banner');
                          return;
                        }
                        setBannerSubmitting(true);
                        setBannerUploading(true);
                        try {
                          // Upload artwork if provided
                          let desktopUrl = null;
                          let mobileUrl = null;

                          if (bannerForm.desktopImage) {
                            desktopUrl = await uploadBannerArtwork(bannerForm.desktopImage, 'desktop');
                          }
                          if (bannerForm.mobileImage) {
                            mobileUrl = await uploadBannerArtwork(bannerForm.mobileImage, 'mobile');
                          }
                          setBannerUploading(false);

                          const pkg = bannerPackages.find(p => p.placement === bannerForm.placement && p.duration_months === bannerForm.duration);

                          // Determine initial status based on artwork
                          const initialStatus = desktopUrl ? 'design_review' : 'pending';

                          await supabase.from('banner_ad_requests').insert({
                            service_provider_id: vendor.id,
                            service_provider_type: vendor.type || 'other',
                            business_name: vendor.name,
                            contact_name: vendor.contact_name || vendor.name,
                            contact_email: vendor.email,
                            contact_phone: vendor.phone,
                            package_id: pkg?.id,
                            package_name: pkg?.display_name || `${bannerForm.placement} Banner`,
                            placement: bannerForm.placement,
                            duration_months: bannerForm.duration,
                            price: pkg?.price || 0,
                            discount_applied: pkg?.discount_percent || 0,
                            final_price: pkg?.price || 0,
                            target_pages: bannerForm.targetPages,
                            ad_image_url: desktopUrl,
                            ad_image_url_mobile: mobileUrl,
                            ad_link_url: bannerForm.linkUrl,
                            ad_alt_text: bannerForm.altText || vendor.name,
                            status: initialStatus,
                            admin_notes: `${bannerForm.notes || ''}\n\nPayment preference: ${bannerForm.payWithListing ? 'Include with listing' : 'Pay separately'}`.trim()
                          });

                          alert('Banner ad request submitted successfully! We\'ll review your request and get back to you shortly.');
                          setShowBannerForm(false);
                          setBannerForm({
                            placement: '',
                            duration: 0,
                            notes: '',
                            targetPages: ['home'],
                            linkUrl: '',
                            altText: '',
                            desktopImage: null,
                            mobileImage: null,
                            payWithListing: false
                          });
                          setDesktopPreview(null);
                          setMobilePreview(null);
                          loadVendorData();
                        } catch (err) {
                          alert('Error submitting request: ' + err.message);
                        } finally {
                          setBannerSubmitting(false);
                          setBannerUploading(false);
                        }
                      }}
                      disabled={bannerSubmitting || !bannerForm.placement || !bannerForm.duration || !bannerForm.linkUrl}
                      className="flex-1 py-4 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white font-bold rounded-xl flex items-center justify-center gap-2"
                    >
                      {bannerSubmitting ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          {bannerUploading ? 'Uploading artwork...' : 'Submitting...'}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Submit Banner Ad Request
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowBannerForm(false);
                        setBannerForm({
                          placement: '',
                          duration: 0,
                          notes: '',
                          targetPages: ['home'],
                          linkUrl: '',
                          altText: '',
                          desktopImage: null,
                          mobileImage: null,
                          payWithListing: false
                        });
                        setDesktopPreview(null);
                        setMobilePreview(null);
                      }}
                      className="px-6 py-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Support */}
        <div className="bg-gray-100 rounded-2xl p-6 text-center">
          <h3 className="font-bold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-600 mb-4">
            If you have any questions about your listing or payments, please contact our support team.
          </p>
          <a
            href={`mailto:${paymentSettings.support_email || 'info@jamaicadriversguide.com'}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-xl font-medium"
          >
            <Mail className="w-5 h-5" />
            {paymentSettings.support_email || 'info@jamaicadriversguide.com'}
          </a>
        </div>
      </div>
    </div>
  );
};

export default VendorPortalPage;
